import Footer from "./Footer";
import { MAIN_ORIGIN, MINECRAFT_ORIGIN, STORE_ORIGIN } from "../utils/siteUrls";

const NAME = "The Ones Above";
const LAST_UPDATED = "23 September 2026";
const linkStyle = { color: "#c9a84c" };

function Section({ id, title, children }) {
  return (
    <section id={id} style={{ marginTop: 34, scrollMarginTop: 90 }}>
      <h2 style={{
        margin: "0 0 12px", color: "#e2d9f3", fontFamily: "'Cinzel',serif",
        fontSize: "clamp(18px,2vw,23px)", letterSpacing: .3,
      }}>{title}</h2>
      <div style={{ color: "#a99dbb", fontSize: 14, lineHeight: 1.8 }}>{children}</div>
    </section>
  );
}

function P({ children }) {
  return <p style={{ margin: "0 0 12px" }}>{children}</p>;
}

function BulletList({ children }) {
  return <ul style={{ margin: "8px 0 14px", paddingLeft: 22 }}>{children}</ul>;
}

export default function ReturnsRefundsPage() {
  return (
    <>
      <main style={{ minHeight: "72vh", background: "#07060b", padding: "78px 20px 80px" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{
            border: "1px solid rgba(123,94,167,.22)", borderRadius: 14,
            background: "linear-gradient(180deg,rgba(123,94,167,.09),rgba(255,255,255,.018))",
            padding: "clamp(24px,5vw,48px)", boxShadow: "0 25px 70px rgba(0,0,0,.28)",
          }}>
            <div style={{ fontFamily: "'Cinzel Decorative',serif", color: "#c9a84c", fontSize: 13, marginBottom: 8 }}>
              {NAME}
            </div>
            <h1 style={{
              margin: 0, color: "#f1eafb", fontFamily: "'Cinzel',serif",
              fontSize: "clamp(30px,5vw,48px)", lineHeight: 1.1,
            }}>Returns &amp; Refunds Policy</h1>
            <div style={{ color: "#716683", fontSize: 12, marginTop: 12 }}>Last updated: {LAST_UPDATED}</div>

            <div style={{
              marginTop: 24, padding: "15px 17px", borderRadius: 9,
              border: "1px solid rgba(201,168,76,.22)", background: "rgba(201,168,76,.055)",
              color: "#c9bfd9", fontSize: 13, lineHeight: 1.7,
            }}>
              This policy deals specifically with purchases made through TOA Apparel and the TOA RP Minecraft store. General use of the community is covered by our <a href={`${MAIN_ORIGIN}/terms`} style={linkStyle}>Terms &amp; Conditions</a>.
            </div>

            <Section title="1. Scope">
              <P>This policy explains how cancellations, returns, delivery problems and refunds are handled for physical TOA Apparel orders and digital purchases supplied for TOA RP.</P>
              <P>Nothing in this policy limits statutory rights or remedies that cannot lawfully be excluded.</P>
            </Section>

            <Section id="apparel" title="2. TOA Apparel — physical goods">
              <P>TOA Apparel is sold through <a href={STORE_ORIGIN} style={linkStyle}>{STORE_ORIGIN.replace(/^https?:\/\//, "")}</a>. Manufacture, fulfilment and shipping may be carried out by a fulfilment partner such as Fourthwall. Available delivery options, charges and estimates are shown during checkout.</P>
              <P>If you are a UK consumer buying eligible goods online, you will generally have a legal right to cancel within 14 days after receiving the goods without giving a reason. After notifying us that you are cancelling, you generally have a further 14 days to return them.</P>
              <P>Where the statutory cancellation right applies, sums due will be refunded in accordance with applicable law, including the cost of standard delivery where required. If you selected a more expensive delivery option, the additional amount above standard delivery does not normally have to be refunded.</P>
            </Section>

            <Section id="physical-returns" title="3. Returning physical items">
              <P>You may be responsible for the direct cost of returning a change-of-mind item unless the item is faulty, damaged, misdescribed or we have agreed otherwise.</P>
              <P>You may inspect an item in the way you reasonably could in a shop. A lawful deduction may be made where handling beyond that has reduced the item's value.</P>
              <P>Certain products can be excluded from change-of-mind cancellation rights where the law permits, including some personalised or custom-made items and certain sealed products once unsealed.</P>
            </Section>

            <Section id="faulty-goods" title="4. Faulty, damaged or misdescribed apparel">
              <P>If an item arrives faulty, damaged, incorrect or materially different from its description, contact us through an official support channel and provide the order details and, where useful, photographs showing the problem.</P>
              <P>Your statutory rights for faulty or misdescribed goods are separate from the change-of-mind cancellation right and are not removed by this policy.</P>
            </Section>

            <Section id="digital" title="5. TOA RP — digital purchases">
              <P>TOA RP purchases may include server ranks, TOA Coins and other in-game entitlements supplied to the Minecraft username associated with the order.</P>
              <P>At checkout, you are asked to request immediate supply and acknowledge that, once supply of digital content begins, you may lose the statutory 14-day cancellation right that would otherwise apply. If the required consent or acknowledgement has not been obtained, your legal position may be different.</P>
              <P>This does not remove remedies that apply where paid digital content is faulty, not as described or not supplied as agreed.</P>
            </Section>

            <Section id="digital-delivery" title="6. Missing or incorrect Minecraft items">
              <P>If a Minecraft purchase has not been delivered, has been delivered to the wrong linked username, or the quantity or entitlement is incorrect, first check <strong style={{ color: "#ddd3ec" }}>Profile → Orders</strong> and then contact an official TOA support channel with the order reference.</P>
              <P>Where appropriate, we may investigate the fulfilment record, re-deliver the entitlement, correct the account grant, or provide another remedy required by law.</P>
            </Section>

            <Section id="coins" title="7. TOA Coins and virtual items">
              <BulletList>
                <li>TOA Coins and other virtual items have no real-world cash value.</li>
                <li>They cannot be cashed out or exchanged for real currency.</li>
                <li>They cannot be transferred for use on another Minecraft server.</li>
                <li>They are not Minecoins and are not issued, sponsored or endorsed by Mojang or Microsoft.</li>
              </BulletList>
              <P>If a payment is lawfully refunded, reversed or charged back, the corresponding virtual currency, rank or entitlement may be removed or adjusted to reflect the reversed transaction.</P>
            </Section>

            <Section id="moderation" title="8. Suspensions, bans and paid server items">
              <P>Purchasing a rank, TOA Coins or another server entitlement does not exempt a player from TOA RP rules or moderation. A suspension or ban may prevent use of server-based benefits for as long as access is restricted.</P>
              <P>A moderation action does not automatically create a right to a refund solely because the player can no longer access the server. This does not affect any separate statutory remedy that applies because a product itself was not supplied as agreed or because applicable law otherwise requires a refund.</P>
            </Section>

            <Section id="refund-method" title="9. Refund method and timing">
              <P>Where a refund is due, it will normally be made to the original payment method unless another lawful method is agreed. The time for the funds to appear can also depend on the payment provider or bank.</P>
              <P>For eligible cancelled physical-goods orders, refunds will be processed within the period required by applicable consumer law. We may be permitted to wait until returned goods are received, or until you provide evidence that they have been sent back, whichever occurs first.</P>
            </Section>

            <Section id="support" title="10. Requesting help">
              <P>For the quickest investigation, provide your TOA account username, order reference and a short explanation of the issue through an official support channel. For Minecraft purchases, include the Minecraft username used for the order.</P>
              <P>Do not post full payment-card details, passwords or other sensitive payment information in a forum thread, Discord channel or support message.</P>
            </Section>

            <Section id="cancellation-form" title="11. Cancellation notice for eligible physical goods">
              <P>If you have a legal right to cancel a physical-goods order, you may send a clear statement containing the following information:</P>
              <div style={{
                border: "1px solid rgba(123,94,167,.2)", background: "rgba(255,255,255,.02)",
                borderRadius: 8, padding: 16, color: "#c7bdd6", whiteSpace: "pre-line", lineHeight: 1.75,
              }}>
{`I give notice that I cancel my contract for the following goods:
Order number:
Goods:
Ordered on / received on:
Name:
Address:
Date:`}
              </div>
            </Section>

            <Section title="12. Related terms">
              <P>Use of the wider {NAME} community, accounts, forums, Minecraft services and other TOA features is governed by our <a href={`${MAIN_ORIGIN}/terms`} style={linkStyle}>Terms &amp; Conditions</a>. TOA RP players must also follow the <a href={`${MINECRAFT_ORIGIN}/rules`} style={linkStyle}>Server Rules</a>.</P>
            </Section>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
